import { useState, useEffect, useMemo } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/lib/supabase";
import {
	PageHeader,
	FilterControls,
	StatisticsCards,
	ReflectionCard,
	EmptyState,
	DeleteConfirmDialog,
} from "./components";
import {
	AlertDialog,
	AlertDialogContent,
	AlertDialogHeader,
	AlertDialogTitle,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogAction,
	AlertDialogCancel,
} from "@/components/primitives";
import { useReflections, useDeleteReflection } from "./hooks";
import { calculateStats } from "./utils";
import type { StatusFilter, SortOption } from "./types";
import type { AnalysisData as DCAnalysisData } from "../DailyCheckIn/types";
import { SuggestedReflection as MultiSuggestedReflection } from "../DailyCheckIn/components/SuggestedReflection";

export function AllReflections() {
	const { user } = useAuth();
	const location = useLocation();
	const [searchTerm, setSearchTerm] = useState("");
	const [statusFilter, setStatusFilter] = useState<StatusFilter>("active");
	const [sortBy, setSortBy] = useState<SortOption>("newest");

	const { reflections, filteredReflections, loading, setReflections, refetch } =
		useReflections(user?.id, { searchTerm, statusFilter, sortBy });

	// Refetch reflections when navigating to this page
	useEffect(() => {
		refetch();
	}, [location.pathname]);

	const [archiveDialogOpen, setArchiveDialogOpen] = useState(false);
	const [reflectionToArchive, setReflectionToArchive] = useState<string | null>(
		null,
	);
	const [archiveLoading, setArchiveLoading] = useState(false);

	const [unarchiveDialogOpen, setUnarchiveDialogOpen] = useState(false);
	const [reflectionToUnarchive, setReflectionToUnarchive] = useState<
		string | null
	>(null);
	const [unarchiveLoading, setUnarchiveLoading] = useState(false);

	const handleArchiveClick = (id: string) => {
		setReflectionToArchive(id);
		setArchiveDialogOpen(true);
	};

	const handleUnarchiveClick = (id: string) => {
		setReflectionToUnarchive(id);
		setUnarchiveDialogOpen(true);
	};

	const confirmArchive = async () => {
		if (!reflectionToArchive || !user?.id) return;
		try {
			setArchiveLoading(true);
			const { error } = await supabase
				.from("reflections")
				.update({ archived: true, is_draft: false })
				.eq("id", reflectionToArchive)
				.eq("user_id", user.id);
			if (error) throw error;
			setReflections(
				reflections.map((r) =>
					r.id === reflectionToArchive
						? { ...r, archived: true, is_draft: false }
						: r,
				),
			);
			setArchiveDialogOpen(false);
			setReflectionToArchive(null);
		} catch (e) {
			console.error("Error archiving reflection:", e);
			alert("Failed to archive reflection. Please try again.");
		} finally {
			setArchiveLoading(false);
		}
	};

	const confirmUnarchive = async () => {
		if (!reflectionToUnarchive || !user?.id) return;
		try {
			setUnarchiveLoading(true);
			const { error } = await supabase
				.from("reflections")
				.update({ archived: false })
				.eq("id", reflectionToUnarchive)
				.eq("user_id", user.id);
			if (error) throw error;
			setReflections(
				reflections.map((r) =>
					r.id === reflectionToUnarchive ? { ...r, archived: false } : r,
				),
			);
			setUnarchiveDialogOpen(false);
			setReflectionToUnarchive(null);
		} catch (e) {
			console.error("Error unarchiving reflection:", e);
			alert("Failed to unarchive reflection. Please try again.");
		} finally {
			setUnarchiveLoading(false);
		}
	};

	const cancelArchive = () => {
		setArchiveDialogOpen(false);
		setReflectionToArchive(null);
	};

	const cancelUnarchive = () => {
		setUnarchiveDialogOpen(false);
		setReflectionToUnarchive(null);
	};

	const {
		deleteDialogOpen,
		setDeleteDialogOpen,
		deleteLoading,
		handleDeleteClick,
		confirmDelete,
		cancelDelete,
	} = useDeleteReflection({
		userId: user?.id,
		reflections,
		setReflections,
	});

	const stats = useMemo(() => calculateStats(reflections), [reflections]);
	const hasFilters = searchTerm !== "" || statusFilter !== "all";

	// Suggested reflections from latest analysis
	const [suggestedAnalysis, setSuggestedAnalysis] =
		useState<DCAnalysisData | null>(null);
	useEffect(() => {
		const loadSuggestions = async () => {
			if (!user?.id) return;
			try {
				const { data, error } = await supabase
					.from("daily_checkin_analysis")
					.select("analysis_data, analysis_date")
					.eq("user_id", user.id)
					.order("analysis_date", { ascending: false })
					.limit(1)
					.maybeSingle();
				if (error || !data) {
					setSuggestedAnalysis(null);
					return;
				}
				const raw = (data as any).analysis_data || {};
				const list = Array.isArray(raw.reflectionSuggestions)
					? raw.reflectionSuggestions
					: Array.isArray(raw.suggestedReflections)
						? raw.suggestedReflections
						: raw.reflectionSuggestion
							? [raw.reflectionSuggestion]
							: [];
				// de-duplicate by similarity (simple word overlap)
				const unique: Array<{
					title: string;
					description: string;
					reasoning?: string;
				}> = [];
				const similar = (a: string, b: string) => {
					const aw = a
						.toLowerCase()
						.split(/\s+/)
						.filter((w) => w.length > 3);
					const bw = b
						.toLowerCase()
						.split(/\s+/)
						.filter((w) => w.length > 3);
					const m = aw.filter((w) =>
						bw.some((x) => x.includes(w) || w.includes(x)),
					).length;
					return m / Math.max(aw.length, 1) > 0.3;
				};
				for (const s of list || []) {
					if (!s?.title || !s?.description) continue;
					if (unique.some((u) => similar(u.title, s.title))) continue;
					unique.push(s);
					if (unique.length >= 3) break;
				}
				if (unique.length === 0) {
					setSuggestedAnalysis(null);
					return;
				}
				setSuggestedAnalysis({
					mainTopics: [],
					negativeTopics: [],
					positiveTopics: [],
					reflectionSuggestion: unique[0] || null,
					reflectionSuggestions: unique,
				});
			} catch (e) {
				setSuggestedAnalysis(null);
			}
		};
		loadSuggestions();
	}, [user?.id]);

	if (loading) {
		return (
			<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
				<div className="container mx-auto px-4 py-8 max-w-7xl">
					<div className="flex items-center justify-center h-64">
						<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/20 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
			<div className="container mx-auto px-4 py-6 sm:py-8 max-w-7xl">
				<PageHeader />

				<FilterControls
					searchTerm={searchTerm}
					onSearchChange={setSearchTerm}
					statusFilter={statusFilter}
					onStatusFilterChange={setStatusFilter}
					sortBy={sortBy}
					onSortByChange={setSortBy}
				/>

				<StatisticsCards stats={stats} />

				{filteredReflections.length === 0 ? (
					<EmptyState hasFilters={hasFilters} />
				) : (
					<div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
						{filteredReflections.map((reflection) => (
							<ReflectionCard
								key={reflection.id}
								reflection={reflection}
								onDelete={handleDeleteClick}
								onArchive={handleArchiveClick}
								onUnarchive={handleUnarchiveClick}
							/>
						))}
					</div>
				)}

				<DeleteConfirmDialog
					open={deleteDialogOpen}
					onOpenChange={setDeleteDialogOpen}
					onConfirm={confirmDelete}
					onCancel={cancelDelete}
					loading={deleteLoading}
				/>

				<AlertDialog
					open={archiveDialogOpen}
					onOpenChange={setArchiveDialogOpen}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Archive Reflection</AlertDialogTitle>
							<AlertDialogDescription>
								Are you sure you want to archive this reflection? You can
								restore it later, but it will be hidden from active lists.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel onClick={cancelArchive}>
								Cancel
							</AlertDialogCancel>
							<AlertDialogAction
								onClick={confirmArchive}
								disabled={archiveLoading}
							>
								{archiveLoading ? "Archiving..." : "Archive"}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				<AlertDialog
					open={unarchiveDialogOpen}
					onOpenChange={setUnarchiveDialogOpen}
				>
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>Unarchive Reflection</AlertDialogTitle>
							<AlertDialogDescription>
								Are you sure you want to unarchive this reflection? It will
								reappear in your active list.
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel onClick={cancelUnarchive}>
								Cancel
							</AlertDialogCancel>
							<AlertDialogAction
								onClick={confirmUnarchive}
								disabled={unarchiveLoading}
							>
								{unarchiveLoading ? "Unarchiving..." : "Unarchive"}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				</AlertDialog>

				{/* Suggested Reflections (bottom) */}
				{suggestedAnalysis && (
					<div className="mt-10">
						<h2 className="text-lg font-medium text-foreground mb-3">
							Suggested Reflections
						</h2>
						<MultiSuggestedReflection
							analysisData={suggestedAnalysis}
							reflections={reflections.filter((r) => !r.archived)}
						/>
					</div>
				)}
			</div>
		</div>
	);
}
